import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import type { MyGroupEntry } from '@pta/types';
import { updateGroup } from '../api/groups';
import { ApiError } from '../api/client';

interface GroupEditModalProps {
  opened: boolean;
  onClose: () => void;
  groupEntry: MyGroupEntry;
  onSaved: () => void;
}

const fullSpan = { gridColumn: '1 / -1' };

/**
 * Csoport-meta szerkesztés a leader-nek. A BE-n a `GroupUpdateSchema` csak
 * `schoolYearId`, `groupTypeId`, `name`, `notes`, `institutionFreeText` mezőt
 * fogad. A binding-status és intézmény-kötés admin út.
 *
 * - free_text csoport: az iskolanév (`institutionFreeText`) szerkeszthető.
 * - claimed/verified csoport: az iskolanév read-only badge + label.
 */
export function GroupEditModal({
  opened,
  onClose,
  groupEntry,
  onSaved,
}: GroupEditModalProps) {
  const [name, setName] = useState(groupEntry.name);
  const [notes, setNotes] = useState(groupEntry.notes ?? '');
  const [institutionFreeText, setInstitutionFreeText] = useState(
    groupEntry.institutionFreeText ?? '',
  );
  const [saving, setSaving] = useState(false);

  const isFreeText = groupEntry.institutionBindingStatus === 'free_text';

  useEffect(() => {
    if (!opened) return;
    setName(groupEntry.name);
    setNotes(groupEntry.notes ?? '');
    setInstitutionFreeText(groupEntry.institutionFreeText ?? '');
  }, [opened, groupEntry]);

  const canSave =
    !saving &&
    name.trim().length > 0 &&
    (!isFreeText || institutionFreeText.trim().length > 0);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload: Parameters<typeof updateGroup>[1] = {
        name: name.trim(),
        notes: notes.trim() || null,
      };
      if (isFreeText) {
        payload.institutionFreeText = institutionFreeText.trim();
      }
      await updateGroup(groupEntry.id, payload);
      notifications.show({ color: 'green', message: 'Csoport frissítve.' });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Csoport szerkesztése"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack>
        {!isFreeText && (
          <Alert color="indigo" variant="light" icon={<IconInfoCircle size={16} />}>
            <Stack gap={4}>
              <Text size="sm">
                <strong>Intézmény:</strong>{' '}
                {groupEntry.institutionLabel ?? '(ismeretlen)'}
              </Text>
              <Group gap="xs">
                <Badge
                  color={
                    groupEntry.institutionBindingStatus === 'verified'
                      ? 'teal'
                      : 'yellow'
                  }
                  variant="light"
                >
                  {groupEntry.institutionBindingStatus === 'verified'
                    ? 'Hitelesített'
                    : 'Igényelt'}
                </Badge>
                <Text size="xs" c="dimmed">
                  Az intézmény-kötés admin által módosítható.
                </Text>
              </Group>
            </Stack>
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          {isFreeText && (
            <TextInput
              style={fullSpan}
              label="Iskola neve (szabad szöveg)"
              value={institutionFreeText}
              onChange={(e) => setInstitutionFreeText(e.currentTarget.value)}
              maxLength={500}
              required
            />
          )}

          <TextInput
            style={fullSpan}
            label="Csoport neve"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={100}
            required
          />

          <Textarea
            style={fullSpan}
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Mentés
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
